import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  StickyNote,
  Plus,
  Save,
  FileText,
  Clock,
  User,
  Trash2,
  Edit3,
  Send
} from 'lucide-react';
import { format } from 'date-fns';

interface Note {
  id: string;
  content: string;
  timestamp: Date;
  author: string;
  type: 'general' | 'player-specific' | 'drill' | 'technique';
  playerId?: string;
  playerName?: string;
}

interface CoachingNotesProps {
  notes: string;
  onAddNote: (note: string) => void;
  sessionNotes?: Note[];
  onSaveNote?: (note: Omit<Note, 'id' | 'timestamp'>) => void;
  onDeleteNote?: (noteId: string) => void;
  onExportNotes?: () => void;
}

export function CoachingNotes({ 
  notes, 
  onAddNote, 
  sessionNotes = [],
  onSaveNote,
  onDeleteNote,
  onExportNotes
}: CoachingNotesProps) {
  const [currentNote, setCurrentNote] = useState('');
  const [noteType, setNoteType] = useState<'general' | 'player-specific' | 'drill' | 'technique'>('general');
  const [selectedPlayer, setSelectedPlayer] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);

  const handleAddNote = () => {
    if (!currentNote.trim()) return;
    
    if (onSaveNote) {
      onSaveNote({
        content: currentNote,
        author: 'Current Coach', // This would come from auth context
        type: noteType,
        playerId: noteType === 'player-specific' ? selectedPlayer : undefined,
        playerName: noteType === 'player-specific' ? 'Player Name' : undefined,
      });
    } else {
      onAddNote(currentNote);
    }
    
    setCurrentNote('');
  };

  const handleQuickNote = (quickNoteText: string) => {
    setCurrentNote(quickNoteText);
  };

  const quickNotes = [
    'Excellent shot placement',
    'Work on footwork',
    'Good timing on drives',
    'Focus on follow-through',
    'Improve bat speed',
    'Watch the ball closer',
    'Better stance needed',
    'Great improvement today'
  ];

  const getNoteTypeColor = (type: string) => {
    switch (type) {
      case 'player-specific':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'drill':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'technique':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <StickyNote className="w-5 h-5 mr-2" />
              Coaching Notes
              {sessionNotes.length > 0 && (
                <Badge variant="outline" className="ml-2">
                  {sessionNotes.length}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Session observations and feedback
            </CardDescription>
          </div>
          
          <div className="flex space-x-2">
            {onExportNotes && (
              <Button variant="outline" size="sm" onClick={onExportNotes}>
                <FileText className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Quick Notes */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Quick Notes</h4>
          <div className="grid grid-cols-2 gap-1">
            {quickNotes.slice(0, isExpanded ? 8 : 4).map((note, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                onClick={() => handleQuickNote(note)}
                className="text-xs h-8 justify-start"
              >
                {note}
              </Button>
            ))}
          </div>
        </div>

        {/* Note Input */}
        <div className="space-y-3">
          <div className="flex space-x-2">
            <select
              value={noteType}
              onChange={(e) => setNoteType(e.target.value as any)}
              className="text-xs px-2 py-1 border rounded"
            >
              <option value="general">General</option>
              <option value="player-specific">Player-specific</option>
              <option value="drill">Drill</option>
              <option value="technique">Technique</option>
            </select>
            
            {noteType === 'player-specific' && (
              <select
                value={selectedPlayer}
                onChange={(e) => setSelectedPlayer(e.target.value)}
                className="text-xs px-2 py-1 border rounded flex-1"
              >
                <option value="">Select Player</option>
                <option value="player-1">Alex Johnson</option>
                <option value="player-2">Sarah Mitchell</option>
                <option value="player-3">James Chen</option>
              </select>
            )}
          </div>
          
          <Textarea
            placeholder="Add coaching observation or note..."
            value={currentNote}
            onChange={(e) => setCurrentNote(e.target.value)}
            className="min-h-[80px] text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                handleAddNote();
              }
            }}
          />
          
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">
              Ctrl+Enter to save quickly
            </span>
            <Button
              size="sm"
              onClick={handleAddNote}
              disabled={!currentNote.trim()}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Note
            </Button>
          </div>
        </div>

        {/* Session Notes Display */}
        {sessionNotes.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Session Notes</h4>
            <ScrollArea className="h-40">
              <div className="space-y-2 pr-2">
                {sessionNotes.map((note) => (
                  <div
                    key={note.id}
                    className="p-2 bg-gray-50 rounded-lg border text-sm"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className={`text-xs ${getNoteTypeColor(note.type)}`}>
                          {note.type.replace('-', ' ')}
                        </Badge>
                        {note.playerName && (
                          <Badge variant="outline" className="text-xs">
                            <User className="w-3 h-3 mr-1" />
                            {note.playerName}
                          </Badge>
                        )}
                      </div>
                      
                      {onDeleteNote && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteNote(note.id)}
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    
                    <p className="text-gray-700 mb-2">{note.content}</p>
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {format(note.timestamp, 'HH:mm')}
                      </span>
                      <span>{note.author}</span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Current Session Summary */}
        {notes && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Session Summary</h4>
            <ScrollArea className="h-32">
              <div className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                {notes || 'No notes yet for this session...'}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-2 pt-2 border-t">
          <Button variant="outline" size="sm" className="flex-1">
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>
          <Button variant="default" size="sm" className="flex-1">
            <Send className="w-4 h-4 mr-2" />
            Share Notes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}